import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Uuid } from 'src/apis/api-utilities';
import { LoadingPage } from 'src/components/loading-page';
import { useCatchError } from 'src/hooks/use-catch-error';
import { assertContext } from 'src/utils/assert-context';
import { refreshCognitoToken } from 'src/utils/axios-instance';
import * as cognito from 'src/utils/cognito';
import { Unauthorized } from 'src/utils/errors';
import { isObject } from 'src/utils/is-object';
import { useMemoOne } from 'use-memo-one';

export type Cognito = typeof cognito;

const CognitoContext = createContext(cognito);

// just for mocking
export const CognitoProvider = CognitoContext.Provider;

export function useCognito() {
  return useContext(CognitoContext);
}

type CognitoUserData = {
  user: CognitoUser;
  /**
   * This is just the _initial_ token. Don't use it for requests.
   * Our Axios interceptors will take care of providing up-to-date tokens for requests.
   */
  token: string;
  /**
   * This is the Cognito user name which is a UUID.
   * This is NOT the actual user ID in our platform.
   */
  cognitoUserName: Uuid;
  session: CognitoUserSession;
};

type CognitoUserContextType = CognitoUserData & { refresh: () => void };

// pass undefined as any, because we run assertContext at runtime
const CognitoUserContext = createContext<CognitoUserContextType | null>(
  undefined as any
);

export function useUserAttributes() {
  const catchError = useCatchError();
  const { getSession } = useCognito();
  const cognitoUser = useCognitoUser();

  const [loading, setLoading] = useState(false);
  const [userAttributes, setUserAttributes] = useState<CognitoUserAttribute[]>(
    []
  );

  useEffect(() => {
    if (!cognitoUser) return;

    const getUserAttributes = async () => {
      setLoading(true);

      try {
        const session = await getSession(cognitoUser.user);

        if (!session.isValid()) {
          throw new Unauthorized();
        }

        cognitoUser.user.getUserAttributes((_err, result) => {
          setUserAttributes(result || []);
          setLoading(false);
        });
      } catch (error) {
        if (isObject(error) && error.code === 'NotAuthorizedException') {
          catchError(new Unauthorized());
        } else {
          catchError(error);
        }
      }
    };

    getUserAttributes();
  }, [cognitoUser, getSession, catchError]);

  return { user: cognitoUser?.user, loading, userAttributes };
}

export const useCognitoUser = () => {
  const context = useContext(CognitoUserContext);
  assertContext(context, 'CognitoUser');
  return context;
};

export const CognitoUserProvider: FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback = <LoadingPage /> }) => {
  const cognito = useCognito();

  // `undefined` means loading
  const [data, setData] = useState<CognitoUserData | null | undefined>();

  const refresh = useCallback(() => {
    refreshCognitoToken(cognito).then(setData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresh on mount
  useEffect(refresh, [refresh]);

  const value = useMemoOne<CognitoUserContextType | null | undefined>(
    () => (data == null ? data : { ...data, refresh }),
    [data, refresh]
  );

  if (value === undefined) return <>{fallback}</>;

  return (
    <CognitoUserContext.Provider value={value}>
      {children}
    </CognitoUserContext.Provider>
  );
};
